'use client'

import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import { useLanguage } from '@/lib/i18n/language-provider'
import { useAuth } from '@/lib/auth-provider'
import { getApiUrl } from '@/lib/api-url'
import { isTauriApp } from '@/lib/tauri-auth'
import {
  Globe,
  Lock,
  Copy,
  Check,
  Loader2,
  Eye,
  Link as LinkIcon,
  ExternalLink,
} from 'lucide-react'

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string | null
  projectName: string
}

interface ShareState {
  is_public: boolean
  share_token: string | null
}

export function ShareDialog({ open, onOpenChange, projectId, projectName }: ShareDialogProps) {
  const { t } = useLanguage()
  const { session } = useAuth()

  const [shareState, setShareState] = useState<ShareState>({ is_public: false, share_token: null })
  const [isLoading, setIsLoading] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const [copied, setCopied] = useState(false)

  // Charger l'état de partage au montage
  const loadShareState = useCallback(async () => {
    if (!projectId || !session?.access_token) return

    setIsLoading(true)
    try {
      const response = await fetch(getApiUrl(`/api/synoptic/projects/${projectId}/share`), {
        headers: { Authorization: `Bearer ${session.access_token}` }
      })

      if (response.ok) {
        const { project } = await response.json()
        setShareState({
          is_public: project.is_public || false,
          share_token: project.share_token || null
        })
      }
    } catch {
      // Silently fail - will show as private
    } finally {
      setIsLoading(false)
    }
  }, [projectId, session?.access_token])

  useEffect(() => {
    if (open) {
      loadShareState()
      setCopied(false)
    }
  }, [open, loadShareState])

  // Toggle public/privé
  const handleTogglePublic = async (checked: boolean) => {
    if (!projectId || !session?.access_token) return

    setIsToggling(true)
    try {
      const response = await fetch(getApiUrl(`/api/synoptic/projects/${projectId}/share`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ is_public: checked })
      })

      if (!response.ok) throw new Error('Toggle failed')

      const { project } = await response.json()
      setShareState({
        is_public: project.is_public,
        share_token: project.share_token
      })

      toast.success(checked ? t('synoptic.share.madePublic') : t('synoptic.share.madePrivate'))
    } catch {
      toast.error(t('synoptic.share.toggleError'))
    } finally {
      setIsToggling(false)
    }
  }

  // Construire le lien de partage — toujours vers visualgrid.app en Tauri
  const shareBaseUrl = isTauriApp() ? 'https://visualgrid.app' : (typeof window !== 'undefined' ? window.location.origin : '')

  const shareUrl = shareState.share_token
    ? `${shareBaseUrl}/synoptique/share/${shareState.share_token}`
    : ''

  // Copier le lien
  const handleCopyLink = async () => {
    if (!shareUrl) return

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success(t('synoptic.share.linkCopied'), {
        description: t('synoptic.share.linkCopiedDesc')
      })
      setTimeout(() => setCopied(false), 3000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = shareUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    }
  }

  if (!projectId) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            {t('synoptic.share.title')}
          </DialogTitle>
          <DialogDescription>
            {t('synoptic.share.description')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Nom du projet */}
            <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-sm font-medium truncate">{projectName}</span>
            </div>

            {/* Toggle Public/Privé */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {shareState.is_public ? (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-green-500/10">
                    <Globe className="h-4 w-4 text-green-500" />
                  </div>
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500/10">
                    <Lock className="h-4 w-4 text-orange-500" />
                  </div>
                )}
                <div>
                  <Label className="text-sm font-medium">
                    {shareState.is_public ? t('synoptic.share.public') : t('synoptic.share.private')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {shareState.is_public ? t('synoptic.share.publicDesc') : t('synoptic.share.privateDesc')}
                  </p>
                </div>
              </div>
              <Switch
                checked={shareState.is_public}
                onCheckedChange={handleTogglePublic}
                disabled={isToggling}
              />
            </div>

            {/* Lien de partage (visible uniquement si public) */}
            {shareState.is_public && shareState.share_token && (
              <>
                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('synoptic.share.viewOnly')}</span>
                    <Badge variant="secondary" className="text-xs">
                      {t('synoptic.share.readOnlyBanner')}
                    </Badge>
                  </div>

                  {/* Champ avec le lien */}
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={shareUrl}
                      className="text-xs font-mono bg-muted/50"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <Button
                      size="sm"
                      variant={copied ? 'default' : 'outline'}
                      className="shrink-0 gap-1.5"
                      onClick={handleCopyLink}
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          {t('synoptic.share.linkCopied')}
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          {t('synoptic.share.copyLink')}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Ouvrir dans un nouvel onglet */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full gap-2 text-xs text-muted-foreground"
                    onClick={() => window.open(shareUrl, '_blank')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('synoptic.share.openInApp')}
                  </Button>
                </div>
              </>
            )}

          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
